<template>
  <b-container fluid class="py-3">
   <b-tabs v-model="tabIndex">
    <b-tab title="Master Report">
    <b-card no-body>
      <b-card-header class="py-2">
        <strong>Master Report</strong>
        <small class="text-muted ml-2">Build a combined report — pick a type, apply filters, view &amp; export.</small>
      </b-card-header>
      <b-card-body class="p-0">
        <!-- ===================== FILTER BAR ===================== -->
        <div class="filter-panel">
        <b-form @submit.prevent="runReport" @reset.prevent="resetForm">

          <!-- Row 1: Report Type | Sub Company -->
          <b-row class="filter-row">
            <b-col md="4">
              <b-form-group label="Report Type" label-size="sm" class="mb-0">
                <b-select
                  size="sm"
                  :options="reportTypes"
                  value-field="value"
                  text-field="text"
                  v-model="form.reportType"
                  @change="onTypeChange"
                ></b-select>
              </b-form-group>
            </b-col>
            <b-col md="8" v-if="isVisible('subCompany')">
              <b-form-group label="Sub Company (उपकंपनी)" label-size="sm" class="mb-0">
                <b-select
                  size="sm"
                  :options="subCompanyOptions"
                  value-field="id"
                  text-field="name"
                  v-model="form.subCompanyId"
                  @change="onSubCompanyChange"
                ></b-select>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Row 2: Region | District -->
          <b-row class="filter-row" v-if="isVisible('region') || isVisible('district')">
            <b-col md="6" v-if="isVisible('region')">
              <b-form-group label="Region (विभाग)" label-size="sm" class="mb-0">
                <b-select
                  size="sm"
                  :options="regionOptions"
                  value-field="id"
                  text-field="name"
                  :disabled="userDistrict != EMPTY_GUID"
                  v-model="form.regionId"
                  @change="onRegionChange"
                ></b-select>
              </b-form-group>
            </b-col>
            <b-col md="6" v-if="isVisible('district')">
              <b-form-group label="District" label-size="sm" class="mb-0">
                <b-select
                  size="sm"
                  :options="districtOptions"
                  value-field="id"
                  text-field="name"
                  :disabled="userDistrict != EMPTY_GUID"
                  v-model="form.districtId"
                ></b-select>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Row 3: Loan Type | Caste -->
          <b-row class="filter-row" v-if="isVisible('loanType') || isVisible('caste')">
            <b-col md="3" v-if="isVisible('loanType')">
              <b-form-group label="Loan Type" label-size="sm" class="mb-0">
                <b-select
                  size="sm"
                  :options="loanTypeOptions"
                  value-field="id"
                  text-field="name"
                  v-model="form.loanType"
                ></b-select>
              </b-form-group>
            </b-col>
            <b-col md="9" v-if="isVisible('caste')">
              <b-form-group label="Caste" label-size="sm" class="mb-0">
                <b-dropdown
                  size="sm"
                  variant="light"
                  block
                  no-caret
                  menu-class="w-100 caste-menu"
                  toggle-class="caste-toggle"
                  :disabled="form.subCompanyId !== ''"
                >
                  <template v-slot:button-content>
                    <span class="caste-btn-text">{{ casteButtonText }}</span>
                    <i class="fa fa-caret-down"></i>
                  </template>
                  <b-dropdown-form class="py-1 px-2">
                    <b-form-input
                      v-model="casteSearch"
                      type="search"
                      size="sm"
                      placeholder="Search caste…"
                      class="mb-2"
                      @click.native.stop
                    ></b-form-input>
                    <div class="d-flex justify-content-between mb-1">
                      <b-button size="sm" variant="link" class="p-0" @click="selectAllCastes">
                        Select all{{ casteSearch ? ' (filtered)' : '' }}
                      </b-button>
                      <b-button size="sm" variant="link" class="p-0 text-danger" @click="clearCastes">
                        Clear
                      </b-button>
                    </div>
                    <div class="caste-list">
                      <b-form-checkbox
                        v-for="opt in filteredCasteOptions"
                        :key="opt.id"
                        v-model="form.casteIds"
                        :value="opt.id"
                        class="caste-item"
                        :class="{ 'caste-item-selected': form.casteIds.indexOf(opt.id) !== -1 }"
                      >{{ opt.name }}</b-form-checkbox>
                      <div v-if="filteredCasteOptions.length === 0" class="text-muted small px-1 py-2">
                        No matching caste.
                      </div>
                    </div>
                  </b-dropdown-form>
                </b-dropdown>
                <small class="text-muted">Leave empty for all.</small>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Row 4: Gender | Age Range -->
          <b-row class="filter-row" v-if="isVisible('gender') || isVisible('age')">
            <b-col md="3" v-if="isVisible('gender')">
              <b-form-group label="Gender" label-size="sm" class="mb-0">
                <b-select
                  size="sm"
                  :options="[{value:'',text:'All'},{value:'Male',text:'Male'},{value:'Female',text:'Female'}]"
                  v-model="form.gender"
                ></b-select>
              </b-form-group>
            </b-col>
            <b-col md="4" v-if="isVisible('age')">
              <b-form-group label="Age Range" label-size="sm" class="mb-0">
                <b-input-group size="sm">
                  <b-form-input
                    type="number" min="0" max="120" placeholder="Min"
                    v-model.number="form.minAge"
                  ></b-form-input>
                  <b-input-group-text>–</b-input-group-text>
                  <b-form-input
                    type="number" min="0" max="120" placeholder="Max"
                    v-model.number="form.maxAge"
                  ></b-form-input>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Row 5: Bank | Status -->
          <b-row class="filter-row" v-if="isVisible('bank') || isVisible('status')">
            <b-col md="5" v-if="isVisible('bank')">
              <b-form-group label="Bank" label-size="sm" class="mb-0">
                <b-select
                  size="sm"
                  :options="bankOptions"
                  value-field="id"
                  text-field="name"
                  v-model="form.bankId"
                ></b-select>
              </b-form-group>
            </b-col>
            <b-col md="7" v-if="isVisible('status')">
              <b-form-group label="Status" label-size="sm" class="mb-0">
                <b-select
                  size="sm"
                  :options="statusOptions"
                  value-field="id"
                  text-field="name"
                  v-model="form.statusId"
                ></b-select>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Row 6: Application No | LOI No | Mobile -->
          <b-row class="filter-row" v-if="isVisible('appNo') || isVisible('loiNo') || isVisible('mobile')">
            <b-col md="4" v-if="isVisible('appNo')">
              <b-form-group label="Application No" label-size="sm" class="mb-0">
                <b-form-input size="sm" v-model="form.applicationNo" placeholder="e.g. APP/2024/001"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4" v-if="isVisible('loiNo')">
              <b-form-group label="LOI No" label-size="sm" class="mb-0">
                <b-form-input size="sm" v-model="form.loiNumber" placeholder="e.g. VJNT/LOI/001"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4" v-if="isVisible('mobile')">
              <b-form-group label="Mobile" label-size="sm" class="mb-0">
                <b-form-input size="sm" v-model="form.mobile" placeholder="10-digit mobile"></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Row 7: From Date | To Date + Search/Reset -->
          <b-row class="filter-row align-items-end">
            <b-col md="3" v-if="isVisible('dates')">
              <b-form-group label="From Date" label-size="sm" class="mb-0">
                <b-form-input
                  type="date" size="sm"
                  :min="selectedCompany ? selectedCompany.est : ''"
                  v-model="form.fromDate"
                ></b-form-input>
                <small v-if="selectedCompany" class="text-muted">Min: {{ Formatter.convertDateFormat(selectedCompany.est) }}</small>
              </b-form-group>
            </b-col>
            <b-col md="3" v-if="isVisible('dates')">
              <b-form-group label="To Date" label-size="sm" class="mb-0">
                <b-form-input type="date" size="sm" v-model="form.toDate"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col class="pt-4">
              <b-button type="submit" variant="primary" :disabled="isLoading" class="mr-2">
                <b-spinner small v-if="isLoading" class="mr-1"></b-spinner>
                <i v-else class="fa fa-search mr-1"></i>
                Search
              </b-button>
              <b-button type="reset" variant="outline-secondary" :disabled="isLoading">Reset</b-button>
            </b-col>
          </b-row>

          <b-row v-if="form.reportType === 'claim'" class="mt-1">
            <b-col md="12">
              <small class="text-info">
                <i class="fa fa-info-circle"></i>
                Claims are shown as a per-loan summary (total claimed/paid amounts), the same
                fast report used under REPORTS &gt; Claims &gt; Claims Report. The From/To dates
                filter by <strong>LOI date</strong> (defaults to the last 30 days). A specific
                district must be selected for the fast report; "All" districts falls back to the
                slower detailed endpoint, which may time out on wide ranges.
              </small>
            </b-col>
          </b-row>
        </b-form>
        </div><!-- /.filter-panel -->

        <div class="px-3 pt-2">
        <b-alert
          :show="showAlert"
          dismissible
          @dismissed="showAlert = false"
          :variant="alertVariant"
          class="mt-2 py-2"
        >{{ alertText }}</b-alert>

        <hr class="my-2" />

        <!-- ===================== RESULT TOOLBAR ===================== -->
        <b-row class="mb-2" align-v="center">
          <b-col md="4">
            <b-input-group size="sm">
              <b-form-input v-model="quickFilter" placeholder="Type to search results"></b-form-input>
              <b-input-group-append>
                <b-btn :disabled="!quickFilter" @click="quickFilter = ''" size="sm">Clear</b-btn>
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col md="4">
            <span style="font-size: 0.85rem">Total Rows: <strong>{{ totalRows }}</strong></span>
          </b-col>
          <b-col md="4" class="text-right" v-if="items.length > 0">
            <json-excel
              :data="items"
              :fields="excelFields"
              :name="excelFileName"
              class="d-inline-block"
            >
              <b-button size="sm" variant="success" class="mr-2">
                <i class="fa fa-file-excel"></i> Excel
              </b-button>
            </json-excel>
            <b-button size="sm" variant="danger" @click="exportPdf">
              <i class="fa fa-file-pdf"></i> PDF
            </b-button>
          </b-col>
        </b-row>

        <div v-if="isLoading" class="text-center py-4">
          <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
          <div class="mt-2">Loading report...</div>
        </div>

        <!-- ===================== RESULT TABLE ===================== -->
        <div v-else class="overflow-auto">
          <b-table
            show-empty
            small
            head-variant="dark"
            empty-text="No records. Choose filters and click Search."
            bordered
            striped
            hover
            responsive
            :items="items"
            :fields="tableFields"
            :current-page="currentPage"
            :per-page="perPage"
            :filter="quickFilter"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            @filtered="onFiltered"
            style="font-size: 12px"
          >
            <template v-slot:cell(__action)="row">
              <b-button size="sm" variant="info" class="py-0" @click="viewRow(row.item)">View</b-button>
            </template>
          </b-table>
        </div>

        <b-row v-if="items.length > 0">
          <b-col md="8" class="my-1">
            <b-pagination
              :total-rows="totalRows"
              :per-page="perPage"
              v-model="currentPage"
              class="my-0"
              size="sm"
            ></b-pagination>
          </b-col>
          <b-col md="4" class="my-1">
            <b-form-group horizontal label="Per page" class="mb-0" label-size="sm" :label-cols="6">
              <b-form-select :options="pageOptions" v-model="perPage" size="sm"></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
        </div><!-- /.px-3 result area -->
      </b-card-body>
    </b-card>
    </b-tab>

    <b-tab title="Sub Companies">
      <sub-companies @select="onCompanyTabSelect"></sub-companies>
    </b-tab>

    <b-tab title="Castes">
      <castes @select="onCasteTabSelect"></castes>
    </b-tab>
   </b-tabs>

    <!-- ===================== VIEW MODAL ===================== -->
    <b-modal id="masterReportView" size="lg" title="Record Details" scrollable hide-footer>
      <table class="table table-sm table-bordered" style="font-size: 12px">
        <tbody>
          <tr v-for="(val, key) in viewItem" :key="key">
            <th style="width: 40%; background:#f5f5f5">{{ key }}</th>
            <td>{{ val }}</td>
          </tr>
        </tbody>
      </table>
    </b-modal>
  </b-container>
</template>

<script>
import apis from "@/apis";
import JsonExcel from "vue-json-excel";
import * as jsPDF from "jspdf";
import "jspdf-autotable";
import Formatter from "@/Formatter";
import { ALL_COMPANIES, getCompanyById } from "@/config/subCompanies";
import SubCompanies from "@/components/AdminReports/SubCompanies";
import Castes from "@/components/AdminReports/Castes";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

// Maharashtra administrative divisions with exact district names as stored in DB.
const MAHARASHTRA_REGIONS = [
  { id: 1, name: "Konkan (कोकण)",            districts: ["Mumbai City","Mumbai Suburban","Thane","Palghar","Raigad","Ratnagiri","Sindhudurg"] },
  { id: 2, name: "Pune (पुणे)",              districts: ["Pune","Kolhapur","Sangli","Satara","Solapur"] },
  { id: 3, name: "Nashik (नाशिक)",           districts: ["Nashik","Ahilyanagar","Dhule","Jalgaon","Nandurbar"] },
  { id: 4, name: "C.S. Nagar (छ.सं.नगर)",   districts: ["Chhatrapati Sambhajinagar","Beed","Parbhani","Jalna"] },
  { id: 5, name: "Latur (लातूर)",            districts: ["Latur","Dharashiv","Nanded","Hingoli"] },
  { id: 6, name: "Amravati (अमरावती)",       districts: ["Amravati","Akola","Buldhana","Washim","Yavatmal"] },
  { id: 7, name: "Nagpur (नागपूर)",          districts: ["Nagpur","Bhandara","Wardha","Chandrapur","Gadchiroli","Gondia"] }
];

export default {
  name: "MasterReport",
  components: { JsonExcel, SubCompanies, Castes },
  data() {
    return {
      EMPTY_GUID,
      Formatter,
      tabIndex: 1,
      reportTypes: [
        { value: "application", text: "Applications" },
        { value: "loi", text: "LOI" },
        { value: "sanction", text: "Sanction Letters" },
        { value: "disbursement", text: "Disbursements" },
        { value: "claim", text: "Claims" },
        { value: "casteWise", text: "Caste Wise" },
        { value: "casteSummary", text: "Caste Summary" }
      ],
      // which filters each report type exposes
      filterMap: {
        application: ["region", "district", "loanType", "subCompany", "caste", "gender", "age", "status", "appNo", "mobile", "dates"],
        loi:         ["region", "district", "subCompany", "caste", "bank", "appNo", "loiNo", "dates"],
        sanction:    ["region", "district", "caste", "bank", "appNo", "loiNo", "dates"],
        disbursement:["region", "district", "loanType", "caste", "bank", "appNo", "loiNo", "dates"],
        claim:       ["region", "district", "loanType", "subCompany", "caste", "gender", "age", "bank", "status", "appNo", "loiNo", "dates"],
        // Same underlying data as Applications - caste is the primary grouping, not just a filter.
        casteWise:   ["region", "district", "loanType", "subCompany", "caste", "gender", "age", "status", "appNo", "mobile", "dates"],
        // No "region" - the aggregate rows have no DistrictName column, and the region
        // filter would wrongly zero out every row if it tried to match on a missing field.
        // District must be a SPECIFIC district for claim totals to be included (see fetchClaimsForSummary).
        casteSummary:["district", "loanType", "subCompany", "caste", "status", "dates"]
      },
      form: {
        reportType: "application",
        regionId: 0,
        districtId: EMPTY_GUID,
        loanType: 1,
        subCompanyId: "",
        casteIds: [],
        gender: "",
        minAge: "",
        maxAge: "",
        bankId: EMPTY_GUID,
        statusId: 0,
        applicationNo: "",
        loiNumber: "",
        mobile: "",
        fromDate: "",
        toDate: ""
      },
      casteSearch: "",

      loanTypeOptions: [
        { id: 1, name: "Personal" },
        { id: 2, name: "Group" },
        { id: 3, name: "Education" }
      ],
      districtsRaw: [],
      castesRaw: [],
      banksRaw: [],
      appStatusRaw: [],
      claimStatusRaw: [],

      items: [],
      tableFields: [],
      currentPage: 1,
      perPage: 50,
      totalRows: 0,
      pageOptions: [50, 100, 500],
      sortBy: null,
      sortDesc: false,
      quickFilter: "",

      viewItem: {},
      isLoading: false,
      showAlert: false,
      alertVariant: "warning",
      alertText: ""
    };
  },
  computed: {
    userDistrict() {
      return this.$store.getters.getUserDistrict;
    },
    districtOptions() {
      const list = this.districtsRaw.map(d => ({
        id: d.DistID || d.DistrictID || d.id,
        name: d.DistName || d.DistrictName || d.name
      }));
      // When a region is selected, narrow the list to that region's districts only.
      const region = MAHARASHTRA_REGIONS.find(r => r.id === this.form.regionId);
      const filtered = region
        ? list.filter(d => region.districts.indexOf(d.name) !== -1)
        : list;
      filtered.unshift({ id: EMPTY_GUID, name: region ? "All (" + region.name + ")" : "All" });
      return filtered;
    },
    casteOptions() {
      // Multi-select: no "All" entry — an empty selection means all castes.
      return this.castesRaw.map(c => ({
        id: c.CasteID || c.id,
        name: c.CasteName || c.name
      }));
    },
    regionOptions() {
      const list = MAHARASHTRA_REGIONS.map(r => ({ id: r.id, name: r.name }));
      list.unshift({ id: 0, name: "All Regions" });
      return list;
    },
    filteredCasteOptions() {
      const q = this.casteSearch.trim().toLowerCase();
      const base = q
        ? this.casteOptions.filter(o => (o.name || "").toLowerCase().indexOf(q) !== -1)
        : this.casteOptions.slice();
      // Show selected castes first so it's clear what is chosen.
      const sel = this.form.casteIds;
      base.sort((a, b) => {
        const av = sel.indexOf(a.id) !== -1 ? 0 : 1;
        const bv = sel.indexOf(b.id) !== -1 ? 0 : 1;
        return av - bv;
      });
      return base;
    },
    casteButtonText() {
      const n = this.form.casteIds.length;
      if (n === 0) return "All castes";
      if (n === 1) {
        const c = this.casteOptions.find(o => o.id === this.form.casteIds[0]);
        return c ? c.name : "1 selected";
      }
      return n + " castes selected";
    },
    bankOptions() {
      const list = this.banksRaw.map(b => ({
        id: b.BankID || b.id,
        name: b.BankName || b.name
      }));
      list.unshift({ id: EMPTY_GUID, name: "All" });
      return list;
    },
    subCompanyOptions() {
      const list = ALL_COMPANIES.map(c => ({
        id: c.id,
        name: c.name + " (est. " + Formatter.convertDateFormat(c.est) + ")"
      }));
      list.unshift({ id: "", name: "सर्व कंपन्या (All Companies)" });
      return list;
    },
    selectedCompany() {
      return this.form.subCompanyId ? getCompanyById(this.form.subCompanyId) : null;
    },
    statusOptions() {
      const raw = this.form.reportType === "claim" ? this.claimStatusRaw : this.appStatusRaw;
      const list = raw.map(s => ({
        id: s.Id != null ? s.Id : (s.ClaimStatusID != null ? s.ClaimStatusID : s.id),
        name: s.ApplicationStatus || s.ClaimStatus || s.name
      }));
      list.unshift({ id: 0, name: "All" });
      return list;
    },
    excelFields() {
      // map { "Column Label": "rowKey" } for vue-json-excel, excluding action col
      const map = {};
      this.tableFields
        .filter(f => f.key !== "__action")
        .forEach(f => {
          map[f.label] = f.key;
        });
      return map;
    },
    excelFileName() {
      const t = (this.reportTypes.find(r => r.value === this.form.reportType) || {}).text || "Report";
      return "MasterReport_" + t.replace(/\s+/g, "") + ".xls";
    }
  },
  mounted() {
    this.loadMasters();
    // Respect a user pinned to one district (RM/DM)
    const ud = this.$store.getters.getUserDistrict;
    if (ud && ud !== EMPTY_GUID) {
      this.form.districtId = ud;
    }
  },
  methods: {
    isVisible(filterKey) {
      const list = this.filterMap[this.form.reportType] || [];
      return list.indexOf(filterKey) !== -1;
    },
    onRegionChange() {
      // Reset district to "All" whenever region changes so the district
      // dropdown re-evaluates to the new region's filtered list.
      this.form.districtId = EMPTY_GUID;
    },
    onTypeChange() {
      // status id meaning differs between app and claim lists; reset to All
      this.form.statusId = 0;
      // sub-company only applies to caste-bearing report types
      if (!this.isVisible("subCompany") && this.form.subCompanyId !== "") {
        this.form.subCompanyId = "";
      }
      // The Claims endpoint returns a very wide, heavy dataset; pulling every
      // claim at once exceeds the API response limit. Pre-fill a recent 30-day
      // window (visible + editable) so it loads reliably out of the box.
      if (this.form.reportType === "claim" && !this.form.fromDate && !this.form.toDate) {
        this.form.fromDate = this.toISODate(this.daysAgo(30));
        this.form.toDate = this.toISODate(new Date());
      }
    },
    onSubCompanyChange() {
      const comp = this.selectedCompany;
      if (comp) {
        // Default the From Date to the establishment date so the report
        // starts from when the company began. The user can move it forward
        // but not before the est. date (enforced by :min on the input).
        //
        // EXCEPTION: Claims already default to a narrow, recent 30-day window
        // (see onTypeChange) because pulling every claim since a company's
        // establishment date returns a huge, heavy dataset that can take too
        // long to load (and appear to hang forever). Don't stomp that safe
        // default here - the :min on the input still stops the user picking
        // a date earlier than the establishment date if they widen it.
        if (this.form.reportType !== "claim") {
          this.form.fromDate = comp.est;
        } else if (!this.form.fromDate) {
          this.form.fromDate = this.toISODate(this.daysAgo(30));
          this.form.toDate = this.form.toDate || this.toISODate(new Date());
        }
        this.form.casteIds = []; // sub-company supersedes the caste picker
      } else {
        this.form.fromDate = "";
      }
    },
    onCompanyTabSelect(companyId) {
      // Triggered from the "Sub Companies" tab "Run Report" button.
      this.form.reportType = "application";
      this.onTypeChange();
      this.form.subCompanyId = companyId;
      this.onSubCompanyChange();
      this.tabIndex = 0;
    },
    onCasteTabSelect(casteId) {
      // Triggered from the "Castes" tab "Run Report" button.
      this.form.reportType = "application";
      this.onTypeChange();
      this.form.subCompanyId = "";
      this.onSubCompanyChange();
      this.form.casteIds = casteId ? [casteId] : [];
      this.tabIndex = 0;
    },
    selectAllCastes() {
      // Add every currently-visible (filtered) caste to the selection.
      const set = {};
      this.form.casteIds.forEach(id => (set[id] = true));
      this.filteredCasteOptions.forEach(o => (set[o.id] = true));
      this.form.casteIds = Object.keys(set);
    },
    clearCastes() {
      this.form.casteIds = [];
    },
    notify(text, variant) {
      this.alertText = text;
      this.alertVariant = variant || "warning";
      this.showAlert = true;
    },
    loadMasters() {
      // Pull the shared master sets used across the admin app (districts/banks/statuses).
      apis.ReadMasters()
        .then(res => {
          if (res.status === 200 && res.body) {
            this.appStatusRaw = res.body.Table || [];
            this.districtsRaw = res.body.Table1 || [];
            this.banksRaw = res.body.Table3 || [];
            this.claimStatusRaw = res.body.Table4 || [];
          }
        })
        .catch(() => {});
      apis.getCaste()
        .then(res => {
          if (res.status === 200) this.castesRaw = res.body || [];
        })
        .catch(() => {});
    },

    // ---- date helpers -------------------------------------------------
    dateOrNull(v) {
      return v ? v : null;
    },
    dateOrDefault(v, def) {
      return v ? v : def;
    },
    daysAgo(n) {
      const d = new Date();
      d.setDate(d.getDate() - n);
      return d;
    },
    toISODate(d) {
      const p = n => (n < 10 ? "0" + n : "" + n);
      return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate());
    },

    // Unwrap a vue-resource list response; throw on non-200 so the caller's
    // catch surfaces a real error instead of silently showing "No records".
    unwrapList(r) {
      if (r && r.status === 200) {
        return Array.isArray(r.body) ? r.body : (r.body || []);
      }
      const detail = r && typeof r.body === "string" ? ": " + r.body : "";
      throw new Error("API returned status " + (r ? r.status : "unknown") + detail);
    },

    // ---- run the selected report -------------------------------------
    runReport() {
      this.showAlert = false;
      this.items = [];
      this.totalRows = 0;
      this.currentPage = 1;
      this.isLoading = true;

      let p;
      switch (this.form.reportType) {
        case "application":
          p = this.runApplications();
          break;
        case "loi":
          p = this.runLOI();
          break;
        case "sanction":
          p = this.runSanction();
          break;
        case "disbursement":
          p = this.runDisbursement();
          break;
        case "claim":
          p = this.runClaim();
          break;
        case "casteWise":
          p = this.runCasteWise();
          break;
        case "casteSummary":
          p = this.runCasteSummary();
          break;
        default:
          p = Promise.resolve([]);
      }

      // Safety net: if the API call hangs (e.g. a heavy query taking too long
      // on the server) don't leave the spinner stuck forever - fail with a
      // clear, actionable message after a reasonable wait.
      const timeout = new Promise((resolve, reject) => {
        setTimeout(() => reject(new Error(
          "Timed out waiting for the report. Try a narrower date range, or filter by district/bank/status to reduce the amount of data requested."
        )), 45000);
      });

      Promise.race([p, timeout]).then(rows => {
        rows = this.applyClientFilters(rows || []);
        this.setResults(rows);
        this.isLoading = false;
      }).catch(err => {
        this.isLoading = false;
        this.notify("Error loading report: " + (err && err.message ? err.message : err), "danger");
      });
    },

    runApplications() {
      const payload = {
        DistrictID: this.form.districtId,
        ApplicationStatusID: this.form.statusId || 0,
        LoanType: this.form.loanType,
        MobileNo: this.form.mobile || "",
        ApplicationNo: this.form.applicationNo || "",
        StartDate: this.dateOrDefault(this.form.fromDate, "1970-01-01"),
        EndDate: this.dateOrDefault(this.form.toDate, "2999-12-31")
      };
      return apis.ReadApplicationListReport(payload).then(r => this.unwrapList(r));
    },

    runCasteWise() {
      // Same data/filters as Applications - the caste breakdown is achieved by
      // sorting the results by caste so same-caste rows are grouped together.
      this.sortBy = "CasteName";
      this.sortDesc = false;
      return this.runApplications();
    },

    // One row per caste: Total Applications, Amount Approved (sum), Total Claims,
    // Total Claim Amount (sum). Claims are joined to applications by LoanApplicationNo
    // to attribute them to a caste (the claims report itself has no caste column).
    runCasteSummary() {
      this.sortBy = "TotalApplications";
      this.sortDesc = true;
      return Promise.all([
        this.runApplications(),
        this.fetchClaimsForSummary().catch(() => null)
      ]).then(([appRows, claimRows]) => {
        const summary = {};
        const casteByAppNo = {};
        const bucket = caste => {
          if (!summary[caste]) {
            summary[caste] = {
              CasteName: caste,
              TotalApplications: 0,
              AmountApproved: 0,
              TotalClaims: 0,
              TotalClaimAmount: 0
            };
          }
          return summary[caste];
        };
        appRows.forEach(r => {
          const caste = r.CasteName || "Unknown";
          const row = bucket(caste);
          row.TotalApplications++;
          row.AmountApproved += Number(r.AmountApproved) || 0;
          if (r.LoanApplicationNo) casteByAppNo[r.LoanApplicationNo] = caste;
        });
        if (claimRows === null) {
          this.notify(
            "Claim totals need a specific district selected (unavailable for 'All' districts). Showing application counts only.",
            "info"
          );
        } else {
          claimRows.forEach(c => {
            const caste = casteByAppNo[c.LoanApplicationNo];
            if (!caste) return; // claim's loan application wasn't in the fetched application list
            const row = bucket(caste);
            row.TotalClaims++;
            row.TotalClaimAmount += Number(c.TotalClaimAmount) || 0;
          });
        }
        return Object.values(summary).sort((a, b) => b.TotalApplications - a.TotalApplications);
      });
    },

    // Reuses the fast per-loan Claims Report (ReportID 12, see runClaim()) instead of
    // the slow/hanging EMI-level endpoint. That report requires a specific district.
    fetchClaimsForSummary() {
      if (!this.form.districtId || this.form.districtId === EMPTY_GUID) {
        return Promise.resolve(null);
      }
      return apis.GetReportDetails({
        BoardID: 1,
        ReportID: 12,
        ReportFilter: {
          district_id: this.form.districtId,
          loan_type: this.form.loanType,
          loi_dt_from: this.form.fromDate || "2001-01-01",
          loi_dt_to: this.form.toDate || "2999-12-31"
        }
      }).then(r => this.unwrapList(r));
    },

    runLOI() {
      // LOI = records in LoanSanction table filtered by LOI issue date.
      // Uses ReadSanctionLetterQuery with LOIStartDate/LOIEndDate so the SP
      // applies the LOI date filter and returns LOINumber, LOIDate, SanctionAmount, CasteName etc.
      const from = this.form.fromDate || "2001-01-01";
      const to   = this.form.toDate   || "2999-12-31";
      const payload = {
        LoanApplicationNo: this.form.applicationNo || null,
        LOINumber:         this.form.loiNumber      || null,
        LOIStartDate:      from,
        LOIEndDate:        to,
        BankID:            this.form.bankId,
        DistrictID:        this.form.districtId,
        UserLoginID:       null,
        RecordStartDate:   "2001-01-01",
        RecordEndDate:     "2999-12-31"
      };
      return apis.ReadSanctionLetterQuery(payload).then(r => this.unwrapList(r));
    },

    runSanction() {
      const payload = {
        LoanApplicationNo: this.form.applicationNo || null,
        LOINumber: this.form.loiNumber || null,
        LOIStartDate: null,
        LOIEndDate: null,
        BankID: this.form.bankId,
        DistrictID: this.form.districtId,
        UserLoginID: null,
        // usp_ReadLoanSanctionByQuery ALWAYS filters UpdatedDate BETWEEN these two,
        // so when the user leaves dates empty we send a wide range = "all".
        RecordStartDate: this.dateOrDefault(this.form.fromDate, "2001-01-01"),
        RecordEndDate: this.dateOrDefault(this.form.toDate, "2999-12-31")
      };
      return apis.ReadSanctionLetterQuery(payload).then(r => this.unwrapList(r));
    },

    runDisbursement() {
      const payload = {
        LoanApplicationNo: this.form.applicationNo || null,
        LOINumber: this.form.loiNumber || null,
        DisbursementStartDate: this.dateOrNull(this.form.fromDate),
        DisbursementEndDate: this.dateOrDefault(this.form.toDate, "2999-12-31"),
        BankID: this.form.bankId,
        DistrictID: this.form.districtId,
        UserLoginID: EMPTY_GUID,
        LoanType: this.form.loanType
      };
      return apis.ReadDisbursementByQuery(payload).then(r => this.unwrapList(r));
    },

    runClaim() {
      // The old EMI-level endpoint (usp_ReadClaimByQuery, date-bounded branch)
      // hangs at the API/Lambda layer no matter how narrow the date range is
      // (confirmed via direct SQL testing: the same query runs in under 2s
      // against the database directly, but the same call through the live
      // API times out). REPORTS > Claims > "Claims Report" uses a different,
      // lightweight per-loan summary query (ReportID 12) that responds in
      // well under a second - reuse that fast path here when a specific
      // district is selected (it requires one; there is no "All districts"
      // mode for this report).
      //
      // NOTE: this fast report returns a DIFFERENT shape than the old one -
      // one row per loan application (with TotalClaimAmount/TotalPaidAmount
      // totals) instead of one row per claim/EMI, and its date range filters
      // by LOI issue date, not claim-update date. Bank/App No/LOI No filters
      // aren't accepted server-side by this report, so they're applied
      // client-side below to keep the existing filter UI working.
      const from = this.form.fromDate || this.toISODate(this.daysAgo(30));
      const to = this.form.toDate || this.toISODate(new Date());

      if (this.form.districtId && this.form.districtId !== EMPTY_GUID) {
        return apis.GetReportDetails({
          BoardID: 1,
          ReportID: 12,
          ReportFilter: {
            district_id: this.form.districtId,
            loan_type: this.form.loanType,
            loi_dt_from: from,
            loi_dt_to: to
          }
        }).then(r => {
          let rows = this.unwrapList(r);
          if (this.form.bankId && this.form.bankId !== EMPTY_GUID) {
            const bank = this.bankOptions.find(b => b.id === this.form.bankId);
            const bankName = bank ? bank.name.toLowerCase() : "";
            if (bankName) rows = rows.filter(row => (row.BankName || "").toLowerCase() === bankName);
          }
          if (this.form.applicationNo) {
            const needle = this.form.applicationNo.toLowerCase();
            rows = rows.filter(row => (row.LoanApplicationNo || "").toLowerCase().indexOf(needle) !== -1);
          }
          if (this.form.loiNumber) {
            const needle = this.form.loiNumber.toLowerCase();
            rows = rows.filter(row => (row.LOINumber || "").toLowerCase().indexOf(needle) !== -1);
          }
          return rows;
        });
      }

      // "All districts" isn't supported by the fast report - fall back to the
      // old (slower, but still timeout-protected) endpoint in that case.
      const payload = {
        LoanApplicationNo: this.form.applicationNo || null,
        LOINumber: this.form.loiNumber || null,
        ClaimStartDate: from,
        ClaimEndDate: to,
        BankID: this.form.bankId,
        DistrictID: this.form.districtId,
        UserLoginID: null,
        ClaimStatus: this.form.statusId || 0,
        LoanType: this.form.loanType,
        StatusOwner: "All"
      };
      return apis.ReadClaimEMIForApproval(payload).then(r => this.unwrapList(r));
    },

    // Caste / sub-company filtering is applied client-side because the reused
    // list endpoints do not accept a caste parameter. It only narrows rows that
    // actually carry a caste field; otherwise it is a no-op (and we warn).
    applyClientFilters(rows) {
      // ---- Sub-company (caste-group) filter --------------------------
      if (this.form.subCompanyId && this.isVisible("subCompany")) {
        const comp = this.selectedCompany;
        if (comp && !comp.isMain) {
          if (!comp.castes || comp.castes.length === 0) {
            this.notify(
              "Sub-company '" + comp.shortName + "' has no caste list configured yet " +
              "(edit src/config/subCompanies.js). Showing all rows in its date range.",
              "info"
            );
          } else {
            // DB caste names carry a category prefix and irregular spacing
            // (e.g. "VJ-A  रामोशी", "SBC - कोष्टी"), so normalise whitespace and
            // match when the row's caste CONTAINS any configured caste token.
            const norm = s => String(s).toLowerCase().replace(/\s+/g, " ").trim();
            const wanted = comp.castes.map(norm).filter(Boolean);
            const casteKeys = ["CasteName", "Caste", "CasteID", "SubCaste", "CategoryName"];
            const hasCasteField =
              rows.length > 0 && casteKeys.some(k => rows[0][k] !== undefined);
            if (hasCasteField) {
              rows = rows.filter(r =>
                casteKeys.some(k => {
                  const v = r[k];
                  if (v == null) return false;
                  const rv = norm(v);
                  return wanted.some(w => rv === w || rv.indexOf(w) !== -1);
                })
              );
            } else {
              this.notify(
                "Sub-company caste filter could not be applied: this report's data " +
                "has no caste column, so only the establishment-date limit was applied.",
                "info"
              );
            }
          }
        }
      }

      // ---- Caste filter (multi-select) -------------------------------
      if (this.form.casteIds && this.form.casteIds.length > 0 && this.isVisible("caste")) {
        const norm = s => String(s).toLowerCase().replace(/\s+/g, " ").trim();
        const selectedIds = this.form.casteIds.map(id => String(id));
        const selectedNames = this.form.casteIds
          .map(id => {
            const c = this.casteOptions.find(o => o.id === id);
            return c ? norm(c.name) : "";
          })
          .filter(Boolean);
        const casteKeys = ["CasteName", "Caste", "CasteID", "SubCaste", "CategoryName"];
        const hasCasteField =
          rows.length > 0 && casteKeys.some(k => rows[0][k] !== undefined);
        if (hasCasteField) {
          rows = rows.filter(r =>
            casteKeys.some(k => {
              const v = r[k];
              if (v === undefined || v === null) return false;
              const s = norm(v);
              return selectedNames.indexOf(s) !== -1 || selectedIds.indexOf(String(v)) !== -1;
            })
          );
        } else {
          this.notify(
            "Caste filter could not be applied: this report's data does not include a caste column. Showing all matching rows.",
            "info"
          );
        }
      }

      // ---- Region filter (client-side, narrows by DistrictName) ----------
      if (this.form.regionId && this.form.regionId !== 0 && this.isVisible("region")) {
        const region = MAHARASHTRA_REGIONS.find(r => r.id === this.form.regionId);
        if (region && this.form.districtId === EMPTY_GUID) {
          // Only apply when "All" district is selected — if a specific district
          // was already chosen it already scopes to one district.
          const wanted = region.districts.map(d => d.toLowerCase());
          const keys = ["DistrictName", "District", "districtName"];
          rows = rows.filter(r =>
            keys.some(k => {
              const v = r[k];
              return v != null && wanted.indexOf(String(v).toLowerCase()) !== -1;
            })
          );
        }
      }

      // ---- Gender filter ---------------------------------------------
      if (this.form.gender && this.isVisible("gender")) {
        const wanted = this.form.gender; // "Male" or "Female"
        rows = rows.filter(r => {
          const g = r.Gender;
          if (g === undefined || g === null) return true; // no Gender column — pass through
          return String(g).toLowerCase() === wanted.toLowerCase();
        });
      }

      // ---- Age Range filter ------------------------------------------
      if (this.isVisible("age") && (this.form.minAge !== "" || this.form.maxAge !== "")) {
        const minA = this.form.minAge !== "" ? Number(this.form.minAge) : null;
        const maxA = this.form.maxAge !== "" ? Number(this.form.maxAge) : null;
        rows = rows.filter(r => {
          const age = r.Age;
          if (age === undefined || age === null) return true; // no Age column — pass through
          const a = Number(age);
          if (minA !== null && a < minA) return false;
          if (maxA !== null && a > maxA) return false;
          return true;
        });
      }

      return rows;
    },

    setResults(rows) {
      this.items = Array.isArray(rows) ? rows : [];
      this.totalRows = this.items.length;
      this.tableFields = this.buildFields(this.items);
      if (this.items.length === 0 && !this.showAlert) {
        this.notify("No records found for the selected filters.", "warning");
      }
    },

    // Build columns from the first row's keys so it works for every endpoint,
    // formatting obvious date columns and appending a View action.
    buildFields(rows) {
      if (!rows || rows.length === 0) return [];
      let keys = Object.keys(rows[0]);
      // Promote the caste column so it shows prominently as the 2nd column,
      // labelled "Caste" (appears whenever the report data includes CasteName).
      if (keys.indexOf("CasteName") > 1) {
        keys = keys.filter(k => k !== "CasteName");
        keys.splice(1, 0, "CasteName");
      }
      const fields = keys.map(k => {
        const label = k === "CasteName" ? "Caste" : this.humanize(k);
        const f = { key: k, label: label, sortable: true };
        if (/date$/i.test(k) || /^date/i.test(k)) {
          f.formatter = v => Formatter.convertDateFormat(v);
        }
        return f;
      });
      fields.push({ key: "__action", label: "View", sortable: false });
      return fields;
    },

    humanize(key) {
      return key
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/_/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase());
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    viewRow(item) {
      this.viewItem = item;
      this.$root.$emit("bv::show::modal", "masterReportView");
    },

    exportPdf() {
      if (this.items.length === 0) return;
      const pdf = new jsPDF("l", "pt");
      const board =
        "Vasantrao Naik Vimukta Jaties and Nomedic Tribes Development Corporation (LTD.)";
      const typeText =
        (this.reportTypes.find(r => r.value === this.form.reportType) || {}).text || "Report";

      pdf.setFontSize(13);
      pdf.text(board.toUpperCase(), pdf.internal.pageSize.width / 2, 28, { align: "center" });
      pdf.setFontSize(11);
      pdf.text(typeText + " Report", pdf.internal.pageSize.width / 2, 46, { align: "center" });

      const cols = this.tableFields
        .filter(f => f.key !== "__action")
        .map(f => ({ header: f.label, dataKey: f.key }));

      pdf.autoTable({ columns: cols, body: this.items, startY: 60, styles: { fontSize: 7 } });
      pdf.save("MasterReport_" + typeText.replace(/\s+/g, "") + ".pdf");
    },

    resetForm() {
      const ud = this.$store.getters.getUserDistrict;
      this.form = {
        reportType: this.form.reportType,
        regionId: 0,
        districtId: ud && ud !== EMPTY_GUID ? ud : EMPTY_GUID,
        loanType: 1,
        subCompanyId: "",
        casteIds: [],
        gender: "",
        minAge: "",
        maxAge: "",
        bankId: EMPTY_GUID,
        statusId: 0,
        applicationNo: "",
        loiNumber: "",
        mobile: "",
        fromDate: "",
        toDate: ""
      };
      this.items = [];
      this.tableFields = [];
      this.totalRows = 0;
      this.quickFilter = "";
      this.casteSearch = "";
      this.showAlert = false;
    }
  }
};
</script>

<style scoped>
.card-header {
  background-color: #0a0032;
  color: #fff;
}

/* ---- Filter panel ---- */
.filter-panel {
  background: #f0f2f8;
  border-bottom: 2px solid #0a0032;
  padding: 14px 16px 6px;
}
.filter-row {
  margin-bottom: 10px;
}

/* ---- Caste multi-select dropdown ---- */
/* ::v-deep pierces BootstrapVue's generated button element */
::v-deep .caste-toggle {
  width: 100% !important;
  text-align: left !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  color: #212529 !important;
  background-color: #fff !important;
  border: 1px solid #ced4da !important;
  font-size: 0.875rem !important;
  padding: 0.25rem 0.5rem !important;
}
::v-deep .caste-toggle:hover {
  background-color: #e9ecef !important;
  border-color: #adb5bd !important;
}
.caste-btn-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #212529;
}
.caste-list {
  max-height: 220px;
  overflow-y: auto;
  padding-right: 4px;
}
.caste-item {
  display: block;
  padding: 3px 6px;
  font-size: 12px;
}
.caste-item-selected {
  background-color: #e8eaf6;
  border-radius: 3px;
  font-weight: 600;
}

/* ---- Result table ---- */
.overflow-auto th, .overflow-auto td {
  white-space: nowrap;
}
</style>
.caste-item-selected >>> .custom-control-label {
  color: #fff;
  font-weight: 600;
}
.caste-btn-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/AdminReports/MasterReport.vue